import axios from "axios";
import { useSetAtom } from "jotai";
import { useContext, useRef, useState } from "react";
import { FormProvider, useForm, useFormContext } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

import { Button } from "@/components/Interface/Button/Button";
import { FormField } from "@/components/Interface/FormFields/FormField/FormField";
import { Modal } from "@/components/Interface/Modal/Modal";
import { ButtonGroupFlexBox } from "@/components/Layout/ButtonGroupFlexBox/ButtonGroupFlexBox";
import { TwoFactorSMSValidation } from "@/components/Welcome/AuthenticatorSetup/AuthenticatorSmsConfirmForm";
import {
  TwoFactorConfirmationCodeForm,
  TwoFactorConfirmationInput,
} from "@/components/Welcome/AuthenticatorSetup/TwoFactorConfirmationInput";
import { useAlert } from "@/hooks/useAlert";
import { useTwoFactorAuthStatus } from "@/hooks/useTwoFactorAuthStatus";
import { DataContext } from "@/provider/DataContextProvider";
import { portalRoutes } from "@/router/routes/portalRoutes";
import { resend, validate } from "@/services/auth";
import { ValidateTwoFactorAuthenticationMethod } from "@/services/model";
import { API_ROOT, CUSTOMER_AUTHORIZATION } from "@/utils/apiUrls.ts";
import {
  accessTokenAtom,
  AlertTypes,
  setAuthAtom,
  showsSessionExpiredModalAtom,
  verifierAtom,
} from "@/utils/atoms";
import { generateVerifier } from "@/utils/authentication";
import { setEsimJourneyRedirectTarget } from "@/utils/esimJourneyRedirect";
import { createReferenceId } from "@/utils/initializeSentry";
import {
  createTwoFactorErrorHandler,
  determineTwoFactorErrorType,
  TwoFactorError,
} from "@/utils/twoFactorErrorHandler";

const extractTwoFactorErrorMessage = (error: any) =>
  error?.response?.data?.message ||
  error?.response?.data?.error_description ||
  error?.response?.data?.error ||
  error?.message ||
  "Unknown error";

const extractTwoFactorErrorStatus = (error: any) =>
  error?.response?.status || error?.status;

export const TwoFactorCodeModal = ({
  userName,
  userPassword,
  method,
  type,
  twoFactorGrantToken,
  setShowModal,
  successAction,
  onAccountLocked,
}: {
  userName?: string;
  userPassword?: string;
  method: keyof typeof ValidateTwoFactorAuthenticationMethod;
  type: "login" | "esim_journey" | "change_2fa";
  twoFactorGrantToken?: string;
  setShowModal: (show: boolean) => void;
  successAction?: () => void;
  onAccountLocked?: () => void;
}) => {
  const { t } = useTranslation();
  const formMethods = useForm<TwoFactorConfirmationCodeForm>({
    reValidateMode: "onSubmit",
  });
  const showToast = useAlert();
  const navigate = useNavigate();

  const [showRecoveryCodeForm, setShowRecoveryCodeForm] = useState(false);
  const setAccessToken = useSetAtom(accessTokenAtom);
  const setAuth = useSetAtom(setAuthAtom);
  const setVerifier = useSetAtom(verifierAtom);
  const setShowSessionExpiredModal = useSetAtom(showsSessionExpiredModalAtom);

  const [, setIsVerified] = useTwoFactorAuthStatus();
  const { dataContext } = useContext(DataContext);

  const isSubmitting = useRef(false);
  const referenceId = createReferenceId();

  const headers = {
    Accept: "application/json, text/plain, */*",
    "Accept-Language": "en,de;q=0.5",
    "Content-Type": "application/x-www-form-urlencoded",
    "X-Requested-With": "XMLHttpRequest",
    Authorization: CUSTOMER_AUTHORIZATION,
    "Sec-Fetch-Dest": "empty",
    "Sec-Fetch-Mode": "cors",
    "Sec-Fetch-Site": "same-origin",
    "Sec-GPC": "1",
    "X-Reference-Id": referenceId,
  };

  // Create centralized error handler
  const handleTwoFactorError = createTwoFactorErrorHandler(
    formMethods.setError,
    t,
    showToast,
    undefined, // setShouldShowTwoFactorModal not needed here
    "code", // codeFieldName
    setAccessToken,
  );

  const verifyAndRedirect = (data: TwoFactorConfirmationCodeForm) => {
    if (isSubmitting.current) return;
    isSubmitting.current = true;

    if (type === "change_2fa" && userName) {
      validate({ code: Number(data.code) })
        .then(() => {
          const hasAppTwoFactorMethod =
            method === ValidateTwoFactorAuthenticationMethod.APP;

          setIsVerified(true);

          if (hasAppTwoFactorMethod) {
            navigate(portalRoutes.user.security.change2faApp);
          } else {
            navigate(portalRoutes.user.security.change2faSms);
          }
        })
        .catch((error) => {
          const twoFactorError: TwoFactorError = {
            message: extractTwoFactorErrorMessage(error),
            status: extractTwoFactorErrorStatus(error),
          };
          handleTwoFactorError(twoFactorError);
          isSubmitting.current = false;
        });
    } else if (userName && userPassword && twoFactorGrantToken) {
      axios
        .post<any>(
          `${API_ROOT}/api-auth-server/oauth/token`,
          new URLSearchParams({
            grant_type: "twoFactorGrantToken",
            username: userName,
            password: userPassword,
            twoFactorCode: data.code,
            twoFactorToken: twoFactorGrantToken,
          }),
          {
            withCredentials: true,
            headers,
          },
        )
        .then((response) => {
          if (response) {
            showToast({
              text: t("portal:user-account.security.2fa.modal.success"),
              type: AlertTypes.success,
            });
          }

          if (!response.data.access_token || !response.data.refresh_token) {
            const tokenError: TwoFactorError = {
              message: "No tokens received after login",
              type: "TOKEN_MISSING_ERROR",
            };
            handleTwoFactorError(tokenError);

            return;
          }

          setAuth({
            accessToken: response.data.access_token,
            refreshToken: response.data.refresh_token,
            isAnonymous: false,
          });

          // Clear session expired modal state on successful 2FA authentication
          setShowSessionExpiredModal(undefined);

          // Generate code-challenge
          const newVerifier = generateVerifier();
          setVerifier(newVerifier);

          setIsVerified(true);

          if (successAction) {
            successAction();
          } else {
            if (type === "esim_journey") {
              setEsimJourneyRedirectTarget("/portal/add-device/4/checkout");
              navigate(
                `/portal/add-device/4/checkout?simId=${dataContext?.basket?.id}`,
                { replace: true },
              );
            } else {
              navigate("/portal/cockpit", { replace: true });
            }
          }
        })
        .catch((error) => {
          const twoFactorError: TwoFactorError = {
            message: extractTwoFactorErrorMessage(error),
            status: extractTwoFactorErrorStatus(error),
          };
          const errorType = determineTwoFactorErrorType(twoFactorError);

          if (errorType === "ACCOUNT_LOCKED" && type === "login") {
            setShowModal(false);
            onAccountLocked?.();
          }

          handleTwoFactorError(twoFactorError);
        })
        .finally(() => {
          isSubmitting.current = false;
        });
    }
  };

  const resendSMS = () => {
    if (userName && userPassword && twoFactorGrantToken) {
      resend({ twoFactorToken: twoFactorGrantToken })
        .then((response) => {
          if (response) {
            showToast({
              text: t(
                "portal:authenticator.sms.verification.resendToast.success",
              ),
              type: AlertTypes.success,
            });
          }
        })
        .catch((error) => {
          const smsError: TwoFactorError = {
            message: extractTwoFactorErrorMessage(error),
            status: extractTwoFactorErrorStatus(error),
            type: "SMS_RESEND_ERROR",
          };
          handleTwoFactorError(smsError);
        });
    }
  };

  const getTitle = () => {
    if (showRecoveryCodeForm) {
      return t("portal:user-account.security.2fa.modal.recoveryTitle");
    }

    const hasSmsTwoFactorMethod =
      method === ValidateTwoFactorAuthenticationMethod.SMS;
    const hasAppTwoFactorMethod =
      method === ValidateTwoFactorAuthenticationMethod.APP;

    if (hasAppTwoFactorMethod) {
      return t("portal:user-account.security.2fa.modal.titleApp");
    } else if (hasSmsTwoFactorMethod) {
      return t("portal:user-account.security.2fa.modal.titleSms");
    }

    return t("portal:user-account.security.2fa.modal.title");
  };

  const title = getTitle();

  const resetAndShowRecoveryCodeForm = (value: boolean) => {
    formMethods.reset();
    setShowRecoveryCodeForm(value);
  };

  return (
    <Modal
      className="max-w-fit"
      title={<h3 className="text-secondary-100">{title}</h3>}
      showCloseButton={false}
      onClose={() => null}
      hasScroll={false}
    >
      <form
        className="flex flex-col gap-10 overflow-y-auto text-black"
        onSubmit={(e) => {
          formMethods.clearErrors();
          formMethods.handleSubmit(verifyAndRedirect)(e);
        }}
      >
        <FormProvider {...formMethods}>
          <div className="flex flex-col gap-6">
            {showRecoveryCodeForm ? (
              <RecoveryCodeForm
                setShowRecoveryCodeForm={resetAndShowRecoveryCodeForm}
              />
            ) : (
              <VerificationCodeForm
                resendAction={resendSMS}
                method={method}
                setShowRecoveryCodeForm={resetAndShowRecoveryCodeForm}
              />
            )}
          </div>
        </FormProvider>
        <ButtonGroupFlexBox
          primaryButton={
            <Button className="accent" type="submit">
              {t("portal:authenticator.sms.verification.verify")}
            </Button>
          }
          secondaryButton={
            <Button
              className="accent inverted"
              type="button"
              onClick={() => setShowModal(false)}
            >
              {t("portal:common.buttons.cancel")}
            </Button>
          }
          forceButtonWidth
        />
      </form>
    </Modal>
  );
};

const TwoFactorAppValidation = () => (
  <div className="flex w-fit flex-col gap-2">
    <TwoFactorConfirmationInput />
  </div>
);

/**
 * Displays a form for verifying the user's identity. Uses their two-factor authentication method,
 * so either SMS or app-based verification method.
 * Allows to switch to the RecoveryCodeForm.
 */
export const VerificationCodeForm = ({
  resendAction,
  method,
  setShowRecoveryCodeForm,
}: {
  resendAction: () => void;
  method: keyof typeof ValidateTwoFactorAuthenticationMethod;
  setShowRecoveryCodeForm: (show: boolean) => void;
}) => {
  const { t } = useTranslation();
  const hasSmsTwoFactorMethod =
    method === ValidateTwoFactorAuthenticationMethod.SMS;
  const hasAppTwoFactorMethod =
    method === ValidateTwoFactorAuthenticationMethod.APP;

  return (
    <>
      <p>{t("portal:user-account.security.2fa.modal.description")}</p>
      {hasSmsTwoFactorMethod && (
        <TwoFactorSMSValidation resendAction={resendAction} />
      )}
      {hasAppTwoFactorMethod && <TwoFactorAppValidation />}

      <p className="flex flex-row flex-wrap gap-2 text-left">
        <span>{t("portal:user-account.security.2fa.modal.unableToUse")}</span>
        <button
          type="button"
          className="w-fit text-left font-semibold text-highlight-100 underline"
          onClick={() => setShowRecoveryCodeForm(true)}
        >
          {t("portal:user-account.security.2fa.modal.useUniqueCode")}
        </button>
      </p>
    </>
  );
};

/**
 * Renders a form for entering a 2FA recovery code.
 * Displays a description, an input field for the recovery code,
 * and a button to switch to the VerificationCodeForm again.
 */
const RecoveryCodeForm = ({
  setShowRecoveryCodeForm,
}: {
  setShowRecoveryCodeForm: (show: boolean) => void;
}) => {
  const { t } = useTranslation();

  const {
    formState: { errors },
    register,
  } = useFormContext<TwoFactorConfirmationCodeForm>();

  return (
    <div className="flex w-fit max-w-[580px] flex-col gap-2">
      <p>
        {t("portal:user-account.security.2fa.modal.recoveryCodeDescription")}
      </p>
      <FormField
        label={t("portal:user-account.security.2fa.modal.recoveryCode")}
        id={"recovery-code"}
        placeholder="000000000000"
        name={"code"}
        errors={errors}
        maxLength={12}
        register={register}
        inputMode="numeric"
        options={{
          required: t("translation:label.validation.required"),
          pattern: {
            value: /^[0-9]{12}$/,
            message: t("portal:user-account.security.2fa.modal.error"),
          },
        }}
      />
      <p className="flex flex-row flex-wrap gap-2 text-left">
        <span>{t("portal:user-account.security.2fa.modal.foundYourCode")}</span>
        <button
          type="button"
          className="w-fit font-semibold text-highlight-100 underline"
          onClick={() => setShowRecoveryCodeForm(false)}
        >
          {t("portal:user-account.security.2fa.modal.title")}
        </button>
      </p>
    </div>
  );
};
